<template>
  <div class="mt-6 flex flex-col gap-4">
    <PaperCard :has_header="false">
      <template #body>
        <div class="mb-4">
          <AsteriskNotice variant="error" size="small" text="Required" class="lg:mb-1" />
          <h3 class="text-base lg:text-lg font-semibold font-inter text-strongest">
            Are you an accredited investor?
          </h3>
        </div>
        <label for="accredited-investor" class="flex items-center cursor-pointer">
          <div
            class="w-3.5 h-3.5 min-w-3.5 min-h-3.5 mt-0.5 rounded flex items-center justify-center"
            :class="
              dataStore.contact.are_you_an_accredited_investor
                ? 'bg-radio-checkbox-checked'
                : 'border border-radio-checkbox-outline'
            "
          >
            <Icon
              v-if="dataStore.contact.are_you_an_accredited_investor"
              name="Check"
              class="h-2.5 w-2.5 text-radio-checkbox-selector"
              :stroke-width="3"
            />
          </div>
          <input
            type="checkbox"
            id="accredited-investor"
            v-model="dataStore.contact.are_you_an_accredited_investor"
            class="sr-only"
          />
          <span class="ml-3 text-strongest font-inter text-sm lg:text-base">
            Yes, I have an annual income greater than $200,000, joint income greater than $300,000
            or net worth greater than $1 million (excluding primary residence).
          </span>
        </label>
      </template>
    </PaperCard>

    <PaperCard :has_header="false">
      <template #body>
        <div
          v-for="(section, index) in filteredEsignatureOptions"
          :key="index"
          class="flex flex-col gap-4"
        >
          <div class="mb-4">
            <AsteriskNotice
              variant="error"
              size="small"
              text="At least one selection is required"
              class="lg:mb-1"
            />
            <h3 class="text-base lg:text-lg font-semibold font-inter text-strongest">
              {{
                section.type.replace(/_/g, " ")[0].toUpperCase() +
                section.type.replace(/_/g, " ").slice(1)
              }}
            </h3>
          </div>
          <div v-for="(option, idx) in section.options" :key="idx" class="flex items-start mb-4">
            <label :for="`option-${index}-${idx}`" class="flex items-center cursor-pointer">
              <div
                class="w-3.5 h-3.5 min-w-3.5 min-h-3.5 mt-0.5 rounded flex items-center justify-center"
                :class="
                  purchaseFlowStore.reg_d_acknowledgements[index > 0 ? idx + 14 : idx]
                    ? 'bg-radio-checkbox-checked'
                    : 'border border-radio-checkbox-outline'
                "
              >
                <Icon
                  v-if="purchaseFlowStore.reg_d_acknowledgements[index > 0 ? idx + 14 : idx]"
                  name="Check"
                  class="h-2.5 w-2.5 text-radio-checkbox-selector"
                  :stroke-width="3"
                />
              </div>
              <input
                type="checkbox"
                :id="`option-${index}-${idx}`"
                v-model="purchaseFlowStore.reg_d_acknowledgements[index > 0 ? idx + 14 : idx]"
                class="sr-only"
              />
              <div class="ml-3">
                <span class="text-strongest font-inter text-sm lg:text-base">
                  {{ option }}
                </span>
                <div
                  v-if="
                    purchaseFlowStore.current_investment.type !== 'Entity' &&
                    idx === 3 &&
                    purchaseFlowStore.reg_d_acknowledgements[3]
                  "
                  class="flex space-x-2 items-center mt-2"
                >
                  <Icon
                    name="Flag"
                    class="h-4 w-4 text-functional-error-default animate-bounce"
                    :stroke-width="2"
                  />
                  <p class="text-red-500 font-medium text-sm">
                    Your current selection is not appropriate for the majority of investors, are you
                    sure?
                  </p>
                </div>
              </div>
            </label>
          </div>
        </div>
      </template>
    </PaperCard>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { PaperCard, AsteriskNotice, Icon } from "@/components";
import { useDataStore, usePurchaseFlowStore } from "@/stores";
import { ESIGNATURE_OPTIONS } from "@/constants";
import { isRetirementAccountType } from "@/utilities";

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();

onMounted(() => {
  window.scrollTo(0, 0);
  if (purchaseFlowStore.current_investment.type === "Entity") {
    purchaseFlowStore.reg_d_acknowledgements =
      purchaseFlowStore.active_entity_account.reg_d_entity_acknowledgements || [];
  } else {
    purchaseFlowStore.reg_d_acknowledgements =
      dataStore.contact.reg_d_personal_acknowledgements || [];
  }
});

const filteredEsignatureOptions = computed(() => {
  const investmentType = purchaseFlowStore.current_investment.type;
  let results = [];
  if (
    investmentType === "Individual" ||
    investmentType === "Joint" ||
    isRetirementAccountType(investmentType)
  ) {
    results = ESIGNATURE_OPTIONS.filter(
      (option) => option.type === "investor_acknowledgement_and_representation"
    );
  } else if (investmentType === "Entity") {
    results = ESIGNATURE_OPTIONS.filter(
      (option) =>
        option.type === "entity_acknowledgements" || option.type === "benefit_plan_investor"
    );
  }
  return results;
});

const areNeededOptionsSelected = computed(() => {
  if (purchaseFlowStore.current_investment.type === "Entity") {
    const first14 = purchaseFlowStore.reg_d_acknowledgements.slice(0, 14);
    const last2 = purchaseFlowStore.reg_d_acknowledgements.slice(14);
    return first14.some((value) => value) && (last2[0] || last2[1]);
  }
  return purchaseFlowStore.reg_d_acknowledgements.some((value) => value);
});

watch(
  filteredEsignatureOptions,
  (newOptions) => {
    const optionsLength = newOptions.reduce((total, section) => total + section.options.length, 0);
    initializeAcknowledgements(optionsLength);
  },
  { immediate: true }
);

function initializeAcknowledgements(optionsLength) {
  if (
    !purchaseFlowStore.reg_d_acknowledgements ||
    purchaseFlowStore.reg_d_acknowledgements.length !== optionsLength
  ) {
    purchaseFlowStore.reg_d_acknowledgements = Array(optionsLength).fill(false);
  }
}
</script>
